/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect, useState } from 'react';
import postAnonymousDeliveryAction, { PostAnonymousDeliveryInput } from '../../../actions/post-anonymous-delivery.action';
import { runActionAndHandleAuth } from '../../../utilities/is-authenticated';
import { AuthContainerProps } from '../lxs-anonymous-delivery.data';
import LoopBanner from './loop-banner';

const AuthContainer: React.FC<AuthContainerProps> = ({ CLASS_NAME, resources, actionContext, loopRegistrationLink, onOrderFound }) => {
    const [postalCode, setPostalCode] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [notFoundError, setNotFoundError] = useState<boolean>(false);
    const [trackingId, setTrackingId] = useState<string>('');

    useEffect(() => {
        const url = new URL(window.location.href);
        setTrackingId(url.searchParams.get('id') || '');
    }, []);

    const successStorageKey = (trackingId: string) => trackingId;
    const submitPostalCode = async (code: string, showError: boolean) => {
        setIsSubmitting(true);
        setNotFoundError(false);

        try {
            const actionInput = new PostAnonymousDeliveryInput(trackingId, code.trim());
            const response = await runActionAndHandleAuth(
                () => postAnonymousDeliveryAction(actionInput, actionContext),
                actionContext.requestContext.user
            );

            const order = response?.success ? response.order : undefined;
            setNotFoundError(!order && showError);
            onOrderFound(order);

            if (order) {
                sessionStorage.setItem(successStorageKey(trackingId), code.trim());
            } else {
                sessionStorage.removeItem(successStorageKey(trackingId));
            }
        } catch (error) {
            setNotFoundError(showError);
            onOrderFound(undefined);
            sessionStorage.removeItem(successStorageKey(trackingId));
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const verifiedPostalCode = sessionStorage.getItem(successStorageKey(trackingId));

        if (verifiedPostalCode) {
            setPostalCode(verifiedPostalCode);
            void submitPostalCode(verifiedPostalCode, false);
        }
    }, [trackingId]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await submitPostalCode(postalCode, true);
    };

    return (
        <div className={`${CLASS_NAME}__auth-wrapper`}>
            <div className={`${CLASS_NAME}__auth-container-zip`}>
                <h2 className={`${CLASS_NAME}__auth-container-zip-title`}>{resources.lxsAnonymousDeliveryTrackTitle}</h2>
                <p className={`${CLASS_NAME}__auth-container-zip-desc`}>{resources.lxsAnonymousDeliveryTrackDescription}</p>

                <form className={`${CLASS_NAME}__auth-container-zip-form`} onSubmit={handleSubmit}>
                    <div className='msc-form__group'>
                        <label className='msc-form-label' htmlFor={`${CLASS_NAME}__auth-container-zip-input`}>
                            {resources.lxsAnonymousDeliveryPostalCodeLabel}
                        </label>
                        <input
                            id={`${CLASS_NAME}__auth-container-zip-input`}
                            className='msc-form-control'
                            value={postalCode}
                            onChange={event => setPostalCode(event.target.value)}
                            placeholder={resources.lxsAnonymousDeliveryPostalCodePlaceholder}
                            required={true}
                            disabled={isSubmitting}
                        />
                    </div>

                    <button className={`msc-cta__primary ${CLASS_NAME}__auth-container-zip-submit`} type='submit' disabled={isSubmitting}>
                        {resources.lxsAnonymousDeliverySubmitButtonText}
                    </button>
                </form>
                <p
                    className={`${CLASS_NAME}__auth-container-zip-alert${
                        notFoundError ? ` ${CLASS_NAME}__auth-container-zip-alert--visible` : ''
                    }`}
                    role='alert'
                    aria-hidden={!notFoundError}
                >
                    {resources.lxsAnonymousDeliveryLookupError}
                </p>

                <p className={`${CLASS_NAME}__auth-container-zip-hint`}>{resources.lxsAnonymousDeliveryPostalCodeHint}</p>
            </div>

            <div className={`${CLASS_NAME}__auth-container-info`}>
                <div className={`${CLASS_NAME}__auth-container-info-box`}>
                    <h4 className={`${CLASS_NAME}__auth-container-info-title`}>{resources.lxsAnonymousDeliveryWhatYouWillSeeTitle}</h4>
                    <ol className={`${CLASS_NAME}__auth-container-info-list`}>
                        <li className={`${CLASS_NAME}__auth-container-info-list-item`}>
                            <span className={`${CLASS_NAME}__auth-container-info-list-index`}>1</span>
                            {resources.lxsAnonymousDeliveryWhatYouWillSeeItem1}
                        </li>
                        <li className={`${CLASS_NAME}__auth-container-info-list-item`}>
                            <span className={`${CLASS_NAME}__auth-container-info-list-index`}>2</span>
                            {resources.lxsAnonymousDeliveryWhatYouWillSeeItem2}
                        </li>
                        <li className={`${CLASS_NAME}__auth-container-info-list-item`}>
                            <span className={`${CLASS_NAME}__auth-container-info-list-index`}>3</span>
                            {resources.lxsAnonymousDeliveryWhatYouWillSeeItem3}
                        </li>
                    </ol>
                </div>

                <div className={`${CLASS_NAME}__auth-container-loop-banner`}>
                    <LoopBanner CLASS_NAME={CLASS_NAME} resources={resources} loopRegistrationLink={loopRegistrationLink} headingTag='h4' />
                </div>
            </div>
        </div>
    );
};

export default AuthContainer;
